/**
 * SponsorPortfolio - shown once a user's application is approved/active.
 * Displays and lets the applicant edit their per-platform entries (livery,
 * skill/safety rating, username, driver profile screenshot, wins, tournaments)
 * plus common info (real name, socials, additional info). Edits flag the
 * application for admin review. Applicant can also request a tier upgrade.
 */

import { useState } from "react";
import { sponsorshipAPI } from "@/lib/api";
import { EQUIPMENT_FIELDS } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, Pencil, Save, X, Plus, ArrowUpRight, Trophy, Gamepad2,
  ExternalLink, Camera, Upload, CheckCircle2, Clock, FileText, Download,
} from "lucide-react";

const SOCIAL_PLATFORMS = ["Twitch", "YouTube", "Facebook", "Instagram", "TikTok", "X/Twitter"];
const LIVERY_ACCEPT =
  "image/png,image/jpeg,image/webp,image/gif,image/bmp,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tga,.dds,.mip,.paint,.zip,.rar";
const isImageData = (data) => typeof data === "string" && data.startsWith("data:image/");
const normSocial = (s) => (typeof s === "object" && s !== null ? { platform: s.platform || SOCIAL_PLATFORMS[0], handle: s.handle || "" } : { platform: SOCIAL_PLATFORMS[0], handle: s || "" });

const TIER_META = {
  grassroots: { label: "Grassroots", cls: "bg-slate-500/20 text-slate-300", order: 0, desc: "Entry-level sponsorship — a Pit Lane branding kit and community perks." },
  rising: { label: "Rising", cls: "bg-amber-500/20 text-amber-400", order: 1, desc: "Confirmed talent — bigger visibility, event access, and gear discounts." },
  pro: { label: "Pro", cls: "bg-primary/20 text-primary", order: 2, desc: "Elite roster — official Pit Lane livery, priority partner deals, and event support." },
  academy: { label: "SRC Academy", cls: "bg-gradient-to-r from-yellow-500/25 to-amber-500/25 text-yellow-200", order: 3, desc: "The SRC Academy — our most elite tier and a sim-to-real academy that prepares proven racers for a real car with real seat time, on-track practice sessions, and test days, not just coaching. Earned on merit — consistency, race craft, pace, and good sportsmanship, not audience size. Invite only." },
};

// Academy is invite / admin-assigned only, so it is never offered as a self-serve "next tier".
const NEXT_TIER = { grassroots: "rising", rising: "pro", pro: null, academy: null };

function entriesFromRequest(request) {
  const src = (Array.isArray(request.platforms) && request.platforms.length)
    ? request.platforms
    : [{
        platform: request.platform || "",
        license_level: request.license_level || "",
        account_link: request.account_link || "",
        livery_image: request.livery_image || "",
        livery_description: request.livery_description || "",
        podiums_wins: request.podiums_wins || "",
        tournaments: request.tournaments || "",
      }];
  return src.map((p) => ({
    platform: p.platform || "",
    license_level: p.license_level || "",
    username: p.username || "",
    profile_screenshot: p.profile_screenshot || "",
    account_link: p.account_link || "",
    wins: (Array.isArray(p.wins) && p.wins.length) ? p.wins.map((w) => ({ name: w.name || "", position: w.position || "" })) : [{ name: "", position: "" }],
    podiums_wins: p.podiums_wins || "",
    tournaments: p.tournaments || "",
    livery_image: p.livery_image || "",
    livery_file_name: p.livery_file_name || "",
    livery_description: p.livery_description || "",
  }));
}

export default function SponsorPortfolio({ request, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const initialSocials = Array.isArray(request.social_links) && request.social_links.length
    ? request.social_links.map(normSocial)
    : (request.account_link ? [normSocial(request.account_link)] : [{ platform: SOCIAL_PLATFORMS[0], handle: "" }]);
  const [form, setForm] = useState({
    real_name: request.real_name || "",
    social_links: initialSocials,
    additional_info: request.additional_info || "",
    platforms: entriesFromRequest(request),
  });

  const tier = TIER_META[request.tier || "grassroots"];
  const nextTierKey = NEXT_TIER[request.tier || "grassroots"];
  const upgradeReq = request.tier_upgrade_request;
  const socials = Array.isArray(request.social_links) ? request.social_links.map(normSocial).filter((s) => s.handle) : [];
  const viewPlatforms = entriesFromRequest(request);

  const setSocialLink = (i, patch) => {
    const next = [...form.social_links];
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, social_links: next });
  };
  const addSocialLink = () => setForm({ ...form, social_links: [...form.social_links, { platform: SOCIAL_PLATFORMS[0], handle: "" }].slice(0, 8) });
  const removeSocialLink = (i) => setForm({ ...form, social_links: form.social_links.filter((_, idx) => idx !== i) });

  const updatePlatform = (i, patch) => setForm((f) => ({
    ...f,
    platforms: f.platforms.map((p, idx) => (idx === i ? { ...p, ...patch } : p)),
  }));

  const addWin = (i) => updatePlatform(i, { wins: [...(form.platforms[i].wins || []), { name: "", position: "" }] });
  const setWin = (i, wi, patch) => {
    const wins = [...(form.platforms[i].wins || [])];
    wins[wi] = { ...wins[wi], ...patch };
    updatePlatform(i, { wins });
  };
  const removeWin = (i, wi) => updatePlatform(i, { wins: (form.platforms[i].wins || []).filter((_, x) => x !== wi) });

  const pickImage = (i, field) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => updatePlatform(i, field === "livery_image" ? { livery_image: ev.target.result, livery_file_name: file.name } : { [field]: ev.target.result });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const save = async () => {
    for (const p of form.platforms) {
      const name = p.platform || "platform";
      if (!p.license_level?.trim()) return toast.error(`Enter skill / safety rating for ${name}.`);
      if (!p.username?.trim()) return toast.error(`Enter your username / profile name for ${name}.`);
      if (!p.profile_screenshot) return toast.error(`Upload a driver profile screenshot for ${name}.`);
      if (!p.tournaments?.trim()) return toast.error(`Enter upcoming tournaments / leagues for ${name}.`);
    }
    setSaving(true);
    try {
      const payload = {
        real_name: form.real_name,
        social_links: form.social_links.filter((s) => s.handle.trim()).map((s) => ({ platform: s.platform, handle: s.handle.trim() })),
        additional_info: form.additional_info,
        platforms: form.platforms.map((p) => ({
          ...p,
          wins: (p.wins || []).filter((w) => (w.name || "").trim() || (w.position || "").trim()).map((w) => ({ name: (w.name || "").trim(), position: (w.position || "").trim() })),
        })),
      };
      await sponsorshipAPI.updateRequest(request.id, payload);
      toast.success("Portfolio updated — SRC will review your changes");
      setEditing(false);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-border bg-card p-6" data-testid={`sponsor-portfolio-${request.id}`}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-xl font-semibold">{request.real_name || request.user_username}</h2>
            <Badge className={`border-0 ${tier.cls}`} data-testid={`sponsor-tier-badge-${request.id}`}>
              <Trophy className="mr-1 h-3 w-3" /> {tier.label}
            </Badge>
            {request.has_pending_updates && (
              <Badge className="border-0 bg-amber-500/20 text-amber-400">
                <Clock className="mr-1 h-3 w-3" /> Pending admin review
              </Badge>
            )}
            {!request.has_pending_updates && (request.status === "approved" || request.status === "active") && (
              <Badge className="border-0 bg-emerald-500/20 text-emerald-400">
                <CheckCircle2 className="mr-1 h-3 w-3" /> {request.status === "active" ? "Active sponsor" : "Approved"}
              </Badge>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{tier.desc}</p>
        </div>
        <div className="flex gap-2">
          {nextTierKey && !upgradeReq && !editing && (
            <Button variant="outline" onClick={() => setUpgradeOpen(true)} data-testid={`sponsor-tier-upgrade-${request.id}`}>
              <ArrowUpRight className="mr-1 h-4 w-4" /> Request {TIER_META[nextTierKey].label}
            </Button>
          )}
          {!editing ? (
            <Button className="bg-primary text-primary-foreground" onClick={() => setEditing(true)} data-testid={`sponsor-edit-${request.id}`}>
              <Pencil className="mr-1 h-4 w-4" /> Edit
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => { setEditing(false); setForm({ real_name: request.real_name || "", social_links: initialSocials, additional_info: request.additional_info || "", platforms: entriesFromRequest(request) }); }} disabled={saving}>Cancel</Button>
              <Button className="bg-primary text-primary-foreground" onClick={save} disabled={saving} data-testid={`sponsor-save-${request.id}`}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save changes</>}
              </Button>
            </>
          )}
        </div>
      </div>

      {upgradeReq && (
        <div className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300" data-testid={`sponsor-upgrade-pending-${request.id}`}>
          <p className="font-semibold">Upgrade to {TIER_META[upgradeReq.target_tier]?.label || upgradeReq.target_tier} pending admin review.</p>
          {upgradeReq.note && <p className="mt-1 text-xs">Note: {upgradeReq.note}</p>}
        </div>
      )}

      {editing ? (
        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label>Real name</Label>
            <Input value={form.real_name} onChange={(e) => setForm({ ...form, real_name: e.target.value })} data-testid={`sponsor-edit-realname-${request.id}`} />
          </div>

          {form.platforms.map((p, i) => (
            <div key={i} className="space-y-3 rounded-lg border border-border bg-secondary/20 p-4" data-testid={`sponsor-edit-platform-${i}`}>
              <div className="flex items-center gap-2">
                <Gamepad2 className="h-4 w-4 text-primary" />
                <p className="text-sm font-bold uppercase tracking-wider text-primary">{p.platform || `Platform ${i + 1}`}</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Skill / Safety Rating *</Label>
                  <Input value={p.license_level} onChange={(e) => updatePlatform(i, { license_level: e.target.value })} placeholder="iRating 2500 / A 4.99 Safety" data-testid={`sponsor-edit-license-${i}`} />
                </div>
                <div className="space-y-1.5">
                  <Label>Username / profile name *</Label>
                  <Input value={p.username} onChange={(e) => updatePlatform(i, { username: e.target.value })} placeholder="Your platform username" data-testid={`sponsor-edit-username-${i}`} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Driver profile screenshot *</Label>
                {p.profile_screenshot ? (
                  <div>
                    <img src={p.profile_screenshot} alt="" className="w-full rounded-md object-cover" />
                    <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-border bg-secondary/40 py-2 text-sm text-primary hover:text-foreground" data-testid={`sponsor-edit-screenshot-change-${i}`}>
                      <Camera className="h-4 w-4" /> Change screenshot
                      <input type="file" accept="image/*" className="hidden" onChange={pickImage(i, "profile_screenshot")} />
                    </label>
                  </div>
                ) : (
                  <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-border bg-secondary/40 p-5 hover:border-primary" data-testid={`sponsor-edit-screenshot-upload-${i}`}>
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15">
                      <Upload className="h-4 w-4 text-primary" />
                    </div>
                    <p className="text-sm font-semibold">Upload profile screenshot</p>
                    <input type="file" accept="image/*" className="hidden" onChange={pickImage(i, "profile_screenshot")} />
                  </label>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Account / profile link</Label>
                <Input value={p.account_link} onChange={(e) => updatePlatform(i, { account_link: e.target.value })} placeholder="Link to your profile" data-testid={`sponsor-edit-account-${i}`} />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5 text-primary" /> Wins / Notable Tournaments</Label>
                  <button type="button" onClick={() => addWin(i)} className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid={`sponsor-edit-add-win-${i}`}>
                    <Plus className="h-3.5 w-3.5" /> Add
                  </button>
                </div>
                {(p.wins || []).map((w, wi) => (
                  <div key={wi} className="flex items-center gap-2">
                    <Input value={w.name} onChange={(e) => setWin(i, wi, { name: e.target.value })} placeholder="Tournament / league name" data-testid={`sponsor-edit-win-name-${i}-${wi}`} />
                    <Input value={w.position} onChange={(e) => setWin(i, wi, { position: e.target.value })} placeholder="Finish" className="max-w-[38%]" data-testid={`sponsor-edit-win-pos-${i}-${wi}`} />
                    {(p.wins || []).length > 1 && (
                      <button type="button" onClick={() => removeWin(i, wi)} className="text-muted-foreground hover:text-foreground">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label>Upcoming Tournaments / Leagues *</Label>
                <Input value={p.tournaments} onChange={(e) => updatePlatform(i, { tournaments: e.target.value })} placeholder="Tournaments you will compete in" data-testid={`sponsor-edit-tournaments-${i}`} />
              </div>
              <div className="space-y-1.5">
                <Label>Livery file</Label>
                {p.livery_image ? (
                  <div>
                    {isImageData(p.livery_image) ? (
                      <img src={p.livery_image} alt="Livery" className="w-full rounded-md object-cover" />
                    ) : (
                      <a href={p.livery_image} download={p.livery_file_name || "livery"} className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2.5 text-sm text-primary" data-testid={`sponsor-edit-livery-file-${i}`}>
                        <FileText className="h-4 w-4 shrink-0" /><span className="flex-1 truncate">{p.livery_file_name || "Livery file"}</span><Download className="h-4 w-4 shrink-0" />
                      </a>
                    )}
                    <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-border bg-secondary/40 py-2 text-sm text-primary hover:text-foreground">
                      <Camera className="h-4 w-4" /> Change file
                      <input type="file" accept={LIVERY_ACCEPT} className="hidden" onChange={pickImage(i, "livery_image")} />
                    </label>
                  </div>
                ) : (
                  <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-border bg-secondary/40 p-6 hover:border-primary">
                    <Camera className="h-6 w-6 text-primary" />
                    <p className="text-sm font-semibold">Upload livery file</p>
                    <p className="text-xs text-muted-foreground">Images or TradingPaints files</p>
                    <input type="file" accept={LIVERY_ACCEPT} className="hidden" onChange={pickImage(i, "livery_image")} />
                  </label>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Livery description</Label>
                <Textarea rows={4} value={p.livery_description} onChange={(e) => updatePlatform(i, { livery_description: e.target.value })} placeholder="Tell us how you plan to modify your livery to include any sponsor(s) you are approved for including placement and size. No livery yet? No problem, tell us what you are planning now and how you'll incorporate the sponsors." data-testid={`sponsor-edit-livery-desc-${i}`} />
              </div>
            </div>
          ))}

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Social / profile links</Label>
              {form.social_links.length < 8 && (
                <button type="button" onClick={addSocialLink} className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                  <Plus className="h-3.5 w-3.5" /> Add
                </button>
              )}
            </div>
            {form.social_links.map((link, i) => (
              <div key={i} className="flex items-center gap-2">
                <select value={link.platform} onChange={(e) => setSocialLink(i, { platform: e.target.value })} className="h-10 shrink-0 rounded-md border border-border bg-background px-2 text-sm" data-testid={`sponsor-edit-social-platform-${i}`}>
                  {SOCIAL_PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <Input value={link.handle} onChange={(e) => setSocialLink(i, { handle: e.target.value })} placeholder="Enter your profile handle link" data-testid={`sponsor-edit-social-${i}-${request.id}`} />
                {form.social_links.length > 1 && (
                  <button type="button" onClick={() => removeSocialLink(i)} className="text-muted-foreground hover:text-foreground">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label>Additional info</Label>
            <Textarea rows={3} value={form.additional_info} onChange={(e) => setForm({ ...form, additional_info: e.target.value })} data-testid={`sponsor-edit-additional-${request.id}`} />
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {viewPlatforms.map((p, i) => (
            <div key={i} className="space-y-3 rounded-lg border border-border bg-secondary/20 p-4" data-testid={`sponsor-view-platform-${i}`}>
              <div className="flex items-center gap-2">
                <Gamepad2 className="h-4 w-4 text-primary" />
                <p className="text-sm font-bold uppercase tracking-wider text-primary">{p.platform || `Platform ${i + 1}`}</p>
              </div>
              {p.livery_image && (
                isImageData(p.livery_image) ? (
                  <img src={p.livery_image} alt="Livery" className="max-h-72 w-full rounded-md object-cover" />
                ) : (
                  <a href={p.livery_image} download={p.livery_file_name || "livery"} className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2.5 text-sm text-primary" data-testid={`sponsor-view-livery-file-${i}`}>
                    <FileText className="h-4 w-4 shrink-0" /><span className="flex-1 truncate">{p.livery_file_name || "Livery file"}</span><Download className="h-4 w-4 shrink-0" />
                  </a>
                )
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Skill / Safety Rating" value={p.license_level} />
                <Field label="Username / profile name" value={p.username} />
                <Field label="Upcoming Tournaments / Leagues" value={p.tournaments} />
              </div>
              {((p.wins || []).some((w) => (w.name || "").trim()) || p.podiums_wins) && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Wins / Notable Tournaments</p>
                  {(p.wins || []).some((w) => (w.name || "").trim()) ? (
                    <ul className="mt-1 list-inside list-disc text-sm">
                      {(p.wins || []).filter((w) => (w.name || "").trim()).map((w, wi) => <li key={wi}>{w.name}{w.position ? ` — ${w.position}` : ""}</li>)}
                    </ul>
                  ) : (
                    <p className="text-sm">{p.podiums_wins}</p>
                  )}
                </div>
              )}
              {p.profile_screenshot && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Driver profile screenshot</p>
                  <img src={p.profile_screenshot} alt="Driver profile" className="mt-1 max-h-72 w-full rounded-md object-cover" />
                </div>
              )}
              {p.account_link && (
                <a href={p.account_link.startsWith("http") ? p.account_link : `https://${p.account_link}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                  <ExternalLink className="h-3 w-3" /> {p.account_link}
                </a>
              )}
              {p.livery_description && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Livery plans</p>
                  <p className="text-sm">{p.livery_description}</p>
                </div>
              )}
            </div>
          ))}
          {(socials.length > 0 || request.account_link) && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Socials &amp; links</p>
              <ul className="mt-1 space-y-1">
                {(socials.length ? socials : [{ platform: "", handle: request.account_link }].filter((s) => s.handle)).map((s, i) => {
                  const h = s.handle || "";
                  return (
                    <li key={i}>
                      <a href={h.startsWith("http") ? h : `https://${h}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                        <ExternalLink className="h-3 w-3" /> {s.platform ? `${s.platform}: ` : ""}{h}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {request.additional_info && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Additional info</p>
              <p className="whitespace-pre-wrap text-sm">{request.additional_info}</p>
            </div>
          )}
          {request.rig_equipment && Object.values(request.rig_equipment).some(Boolean) && (
            <div data-testid={`sponsor-view-rig-${request.id}`}>
              <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                <Gamepad2 className="h-3.5 w-3.5 text-primary" /> Rig / setup
              </p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {EQUIPMENT_FIELDS.filter((f) => request.rig_equipment[f.key]).map((f) => (
                  <div key={f.key} className="rounded-md border border-border bg-secondary/40 p-3 text-sm">
                    <p className="text-[10px] uppercase text-muted-foreground">{f.label}</p>
                    <p className="font-medium">{request.rig_equipment[f.key]}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {nextTierKey && (
        <UpgradeDialog
          open={upgradeOpen}
          onOpenChange={setUpgradeOpen}
          currentTier={request.tier || "grassroots"}
          targetTier={nextTierKey}
          onSubmit={async (note) => {
            try {
              await sponsorshipAPI.requestTierUpgrade(request.id, { target_tier: nextTierKey, note });
              toast.success("Upgrade request submitted — SRC will review");
              setUpgradeOpen(false);
              onChanged?.();
            } catch (err) {
              toast.error(err.response?.data?.detail || "Could not submit request");
            }
          }}
        />
      )}
    </Card>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div className="rounded-md border border-border bg-secondary/40 p-3 text-sm">
      <p className="text-[10px] uppercase text-muted-foreground">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  );
}

function UpgradeDialog({ open, onOpenChange, currentTier, targetTier, onSubmit }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border bg-card">
        <DialogHeader>
          <DialogTitle>
            Request tier upgrade: {TIER_META[currentTier].label} → {TIER_META[targetTier].label}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{TIER_META[targetTier].desc}</p>
        <div className="space-y-1.5 pt-2">
          <Label>Why should you be promoted? (optional)</Label>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Recent podiums, follower growth, notable events..." data-testid="sponsor-upgrade-note" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button className="bg-primary text-primary-foreground" onClick={async () => { setBusy(true); try { await onSubmit(note); } finally { setBusy(false); } }} disabled={busy} data-testid="sponsor-upgrade-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
